"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

export interface CapabilityTableProps {
	translationNamespace?: string;
}

export default function CapabilityTable({ translationNamespace = "capabilityTable" }: CapabilityTableProps) {
	const t = useTranslations(translationNamespace);
	const { openModalInquiry } = useModalInquiryContext();

	// 表格数据配置，每行可以有多个值（多行显示）
	const tableRows = [
		{
			labelKey: "generalTolerancesLabel",
			values: ["generalTolerancesValue1", "generalTolerancesValue2"],
		},
		{
			labelKey: "precisionTolerancesLabel",
			values: ["precisionTolerancesValue"],
		},
		{
			labelKey: "minWallThicknessLabel",
			values: ["minWallThicknessValue"],
		},
		{
			labelKey: "minEndMillSizeLabel",
			values: ["minEndMillSizeValue"],
		},
		{
			labelKey: "minDrillSizeLabel",
			values: ["minDrillSizeValue"],
		},
		{
			labelKey: "maximumPartSizeLabel",
			values: ["maximumPartSizeValue1", "maximumPartSizeValue2"],
		},
		{
			labelKey: "minimumPartSizeLabel",
			values: ["minimumPartSizeValue1", "minimumPartSizeValue2"],
		},
		{
			labelKey: "productionVolumeLabel",
			values: ["productionVolumeValue1", "productionVolumeValue2", "productionVolumeValue3"],
		},
		{
			labelKey: "leadTimeLabel",
			values: ["leadTimeValue1", "leadTimeValue2"],
		},
	];

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("description")}</p>
				</div>

				{/* 表格区域 */}
				<div className="">
					{/* 表头 */}
					<div className="grid grid-cols-[140px_1fr] gap-x-8 border-b-2 border-gray-200 pb-3 sm:grid-cols-[160px_1fr] sm:gap-x-12 md:grid-cols-[180px_1fr] md:gap-x-16">
						<div className="text-sm font-bold text-black sm:text-base">{t("descriptionHeader")}</div>
						<div className="text-sm font-bold text-black sm:text-base">{t("capabilitiesHeader")}</div>
					</div>

					{/* 表格内容 */}
					<div className="divide-y divide-gray-200">
						{tableRows.map((row, index) => (
							<div
								key={index}
								className="grid grid-cols-[140px_1fr] gap-x-8 py-4 sm:grid-cols-[160px_1fr] sm:gap-x-12 md:grid-cols-[180px_1fr] md:gap-x-16 md:py-5"
							>
								{/* 左侧标签 */}
								<div className="pr-4 text-sm font-bold text-black sm:text-base">{t(row.labelKey)}</div>
								{/* 右侧内容 */}
								<div className="flex flex-col gap-1">
									{row.values.map((valueKey, valueIndex) => (
										<p key={valueIndex} className="text-sm text-gray-900 md:text-base">
											{t(valueKey)}
										</p>
									))}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* 按钮区域 */}
				<div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row md:mt-12">
					<Link
						href="/upload"
						className="inline-flex min-w-[180px] items-center justify-center rounded border-2 border-themePrimary400 bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:border-[#83b751] hover:bg-opacity-80 hover:text-white sm:min-w-[200px] sm:px-8 sm:text-base"
					>
						{t("getInstantQuote")}
					</Link>
					<button
						onClick={openModalInquiry}
						className="inline-flex min-w-[180px] items-center justify-center rounded border-2 border-themePrimary400 bg-white px-6 py-3 text-sm font-semibold text-themePrimary400 transition-all duration-300 hover:bg-themePrimary400 hover:text-white sm:min-w-[200px] sm:px-8 sm:text-base"
					>
						{t("uploadYourDesign")}
					</button>
				</div>
			</div>
		</section>
	);
}
